import { formatDate } from "./format";
import i18n from "@/i18n";
import { currentLang, currentLocale } from "@/i18n/languages";
import { fmtNumber, fmtPct as chartPct, gapDigits } from "./engineCharts";
import SAMPLE from "./engineSample.json";

// Logica pura de la consola QCapital Engine. Las comprobaciones replican las de
// backend/services/engine_payload.py para avisar ANTES de calcular; el backend las
// repite porque los activos sin historico se caen despues.

// Los textos salen del catalogo `engine` en el idioma activo al llamar, no al importar.
const t = (key, params) => i18n.t(key, { ns: "engine", ...params });
const ES = i18n.getFixedT("es", "engine");

export const disclaimer = () => t("lib.disclaimer");

export const defaultConstraints = () => ({
    k_max: 20, k_min: 1, w_min_pct: 1, w_max_pct: 10, group_caps_pct: {},
    max_vol_annual_pct: null, risk_aversion: 2, turnover_cost_bps: 10,
    max_turnover_pct: null, lookback_days: 365, time_limit_sec: 20,
});

export const parseNum = (v, lang = currentLang()) => parseFloat(
    lang === "en" ? String(v ?? "").replace(/,/g, "") : String(v ?? "").replace(",", "."),
) || 0;

// Mismo patron que UniverseItem.symbol en backend/models/engine.py: si cambia alli, cambialo aqui.
const SYMBOL_RE = /^[A-Za-z0-9.\-=^_]{1,24}$/;

// Como parseNum, pero un texto que no es un número da NaN en vez de 0: así el aviso lo ve el asesor.
// En en la coma solo es separador de miles bien formado: "2,5" valdria 25 en silencio.
const EN_THOUSANDS_RE = /^[+-]?\d{1,3}(,\d{3})+(\.\d+)?$/;
const enDecimalText = (raw) => {
    if (!raw.includes(",")) return raw;
    const t = raw.replace(/\s/g, "");
    return EN_THOUSANDS_RE.test(t) ? t.replace(/,/g, "") : "x";
};

export function parseNumStrict(v, lang = currentLang()) {
    const raw = String(v ?? "").trim();
    const t = lang === "en" ? enDecimalText(raw) : raw.replace(",", ".");
    if (t === "") return 0;
    return /^[+-]?(\d+\.?\d*|\.\d+)$/.test(t) ? Number(t) : NaN;
}

// Un NaN en las restricciones es texto no numérico: guardarlo lo convertiría en null («sin límite»).
export const hasNonNumeric = (c) =>
    Object.entries(c || {}).some(([k, v]) => (k === "group_caps_pct"
        ? Object.values(v || {}).some((x) => Number.isNaN(x))
        : typeof v === "number" && Number.isNaN(v)));

// Lo mismo que exige EngineConstraints en el servidor, sin necesitar el universo.
export const coherentConstraints = (c) => c.k_min <= c.k_max && c.w_min_pct <= c.w_max_pct && c.k_min * c.w_min_pct <= 100 + 1e-9;

// Sin separador de miles, como antes ("1000"); la coma o el punto decimal, segun el idioma.
const num = (x) => (Math.round(x * 100) / 100).toLocaleString(currentLocale(), { useGrouping: false, maximumFractionDigits: 2 });

// Lista corta para un aviso: los cinco primeros y "…" si hay mas.
const shortList = (items) => `${items.slice(0, 5).join(", ")}${items.length > 5 ? "…" : ""}`;

// Rangos de EngineConstraints en backend/models/engine.py: si cambian allí, cámbialos aquí.
const RANGES = [
    { key: "k_max", min: 1, max: 100 },
    { key: "k_min", min: 1, max: 100 },
    { key: "w_min_pct", min: 0, max: 100 },
    { key: "w_max_pct", min: 0, max: 100, openMin: true },
    { key: "max_vol_annual_pct", min: 0, max: 100, openMin: true, nullable: true },
    { key: "risk_aversion", min: 0, max: 1000 },
    { key: "turnover_cost_bps", min: 0, max: 500 },
    { key: "max_turnover_pct", min: 0, max: 200, openMin: true, nullable: true },
];

function rangeIssue({ key, min, max, openMin }, v) {
    const label = t(`lib.range.label.${key}`);
    if (typeof v !== "number" || !Number.isFinite(v)) return t("lib.range.notNumber", { label });
    if (v > max || (openMin ? v <= min : v < min))
        return t(openMin ? "lib.range.open" : "lib.range.closed", { label, min: num(min), max: num(max) });
    return null;
}

// Capacidad por grupos, igual que check_feasible del backend: cada activo aporta como mucho
// max(su peso actual, w_max) y un grupo con tope, max(lo actual del grupo, min(tope, suma)).
function groupCapacityIssue(c, universe, currentWeights) {
    const wMax = c.w_max_pct;
    if (!(wMax > 0) || !universe.length) return null;
    const caps = c.group_caps_pct || {};
    const capacity = {};
    const held = {};
    universe.forEach((u) => {
        const g = groupOf(u);
        const cur = Number(currentWeights?.[u.symbol]);
        const w = Number.isFinite(cur) && cur > 0 ? cur : 0;
        capacity[g] = (capacity[g] || 0) + Math.max(w, wMax);
        held[g] = (held[g] || 0) + w;
    });
    const total = Object.entries(capacity).reduce((s, [g, cap]) =>
        s + (g in caps ? Math.max(held[g], Math.min(caps[g], cap)) : cap), 0);
    return total < 100 - 1e-9 ? t("lib.groups.capacity", { wMax: num(wMax), total: num(total) }) : null;
}

// Los valores no numéricos llegan como NaN (parseNumStrict). currentWeights: {símbolo: peso %}.
export function validateConstraints(c, universe, currentWeights = {}) {
    const msgs = [];
    const n = universe.length;
    RANGES.forEach((r) => {
        if (r.nullable && c[r.key] == null) return;
        const m = rangeIssue(r, c[r.key]);
        if (m) msgs.push(m);
    });
    const known = new Set(groupsOf(universe));
    Object.entries(c.group_caps_pct || {}).forEach(([g, cap]) => {
        if (!known.has(g)) { msgs.push(t("lib.groups.unknown", { group: g })); return; }
        if (typeof cap !== "number" || !Number.isFinite(cap)) msgs.push(t("lib.groups.capNotNumber", { group: g }));
        else if (cap < 0 || cap > 100) msgs.push(t("lib.groups.capRange", { group: g }));
    });
    if (c.k_min > c.k_max) msgs.push(t("lib.constraints.kMinOverMax"));
    if (c.w_min_pct > c.w_max_pct) msgs.push(t("lib.constraints.wMinOverMax"));
    if (Math.min(c.k_max, n) * c.w_max_pct < 100 - 1e-9)
        msgs.push(t("lib.constraints.notFullyInvested", { n: Math.min(c.k_max, n), wMax: c.w_max_pct }));
    else {
        const capIssue = groupCapacityIssue(c, universe, currentWeights);
        if (capIssue) msgs.push(capIssue);
    }
    if (c.k_min * c.w_min_pct > 100 + 1e-9) msgs.push(t("lib.constraints.minWeightsOver"));
    groupsOf(universe).filter((g) => g.length > 40).forEach((g) =>
        msgs.push(t("lib.groups.nameTooLong", { group: g.slice(0, 40) })));
    const seen = new Set();
    const dup = new Set();
    universe.forEach((u) => {
        const key = String(u.symbol || "").toUpperCase();
        if (seen.has(key)) dup.add(String(u.symbol).toUpperCase());
        seen.add(key);
    });
    if (dup.size) msgs.push(t("lib.symbols.duplicate", { list: [...dup].join(", ") }));
    const bad = universe.map((u) => u.symbol).filter((s) => !SYMBOL_RE.test(String(s || "")));
    if (bad.length) msgs.push(t("lib.symbols.invalid", { list: shortList(bad) }));
    return msgs;
}

const CLIENT_REF_RE = /^[A-Za-z0-9-]{3,20}$/;
export const clientRefIssue = (ref) =>
    !ref || CLIENT_REF_RE.test(ref) ? null : t("lib.clientRef");

export const csvTemplate = () =>
    "symbol,isin,name,asset_class,group\nIWDA.AS,IE00B4L5Y983,iShares Core MSCI World,etf,Renta variable\n";

export function parseUniverseCsv(text) {
    const clean = String(text || "").replace(/^﻿/, "");
    if (clean.includes('"')) return { rows: [], skipped: 0, error: t("lib.csv.quotes") };
    const lines = clean.split(/\r?\n/);
    const header = lines.shift() || "";
    const sep = header.includes(";") && !header.includes(",") ? ";" : ",";
    const cols = header.split(sep).map((h) => h.trim().toLowerCase());
    const idx = (name) => cols.indexOf(name);
    if (idx("symbol") === -1) return { rows: [], skipped: 0, error: t("lib.csv.noSymbol") };
    const rows = [];
    const isinDropped = [];
    let skipped = 0;
    lines.forEach((line) => {
        if (!line.trim()) return;
        const cells = line.split(sep).map((x) => x.trim());
        const symbol = cells[idx("symbol")] || "";
        if (!symbol) { skipped += 1; return; }
        const isin = (cells[idx("isin")] || "").toUpperCase();
        if (isin && !isValidIsin(isin)) isinDropped.push(symbol);
        rows.push({
            symbol,
            isin: isin && isValidIsin(isin) ? isin : null,
            name: cells[idx("name")] || null,
            asset_class: cells[idx("asset_class")] || null,
            group: groupOf({ group: cells[idx("group")] }),
        });
    });
    return { rows, skipped, isinDropped, error: null };
}

// Mismo criterio que UniverseItem en el backend: sin espacios a los lados y, vacío, "Sin grupo".
export const groupOf = (u) => String(u?.group ?? "").trim() || "Sin grupo";

export const groupsOf = (universe) => [...new Set(universe.map(groupOf))].sort();

// "renta variable" y "Renta variable" son el mismo grupo: se reutiliza la grafía que ya existe.
export function canonicalGroup(group, universe) {
    const g = String(group ?? "").trim();
    if (!g) return "";
    return groupsOf(universe).find((x) => x.toLowerCase() === g.toLowerCase()) || g;
}

// ISIN con su dígito de control (Luhn), como core/isin.py: uno que no cuadra no puede existir.
export function isValidIsin(isin) {
    const s = String(isin || "").trim().toUpperCase();
    if (!/^[A-Z]{2}[A-Z0-9]{9}[0-9]$/.test(s)) return false;
    const digits = s.split("").map((c) => (/[A-Z]/.test(c) ? String(c.charCodeAt(0) - 55) : c)).join("");
    let total = 0;
    let double = true;
    for (let i = digits.length - 2; i >= 0; i -= 1) {
        let n = Number(digits[i]) * (double ? 2 : 1);
        if (n > 9) n -= 9;
        total += n;
        double = !double;
    }
    return (10 - (total % 10)) % 10 === Number(digits[digits.length - 1]);
}

// "12 pb (0,12 %)": los puntos básicos solos no los entiende todo el mundo.
export const formatGap = (bps) => {
    const b = Number(bps);
    const lang = currentLang();
    const pb = fmtNumber(b, lang, { grouping: "locale", min: 0, max: gapDigits(b) });
    const pct = fmtNumber(b / 100, lang, { grouping: "locale", sig: 2 });
    return t("lib.gap", { bps: pb, pct });
};

const STATUSES = ["queued", "running", "done", "failed"];
export const statusLabel = (s) => (STATUSES.includes(s) ? t(`lib.status.${s}`) : s);
export const isFinalStatus = (s) => s === "done" || s === "failed";

export function engineAccess(error) {
    if (!error) return { member: true, code: null, message: null };
    const detail = error.response?.data?.detail;
    if (error.response?.status === 403 && detail && typeof detail === "object")
        return { member: false, code: detail.code || null, message: detail.message || null };
    return { member: false, code: "error", message: t("lib.access.loadError") };
}

// Candado de datos (spec de la firma online del piloto): los mismos textos que el backend.
export const docsLockedMessage = () => t("lib.docs.locked");
export const docsPreparingMessage = () => t("lib.docs.preparing");
export const docsAskOwnerMessage = () => t("lib.docs.askOwner");
// Para el 403 docs_unsigned, que llega sin saber en qué fase está el despacho.
export const docsLockedNeutral = () => t("lib.docs.lockedNeutral");

export const isLocked = (me) => Boolean(me?.docs?.locked);

// Texto del candado según la fase real: asistente de firma apagado ("preparando"), responsable
// que puede firmar, o miembro que debe pedirlo. Misma lógica que docsBanner de engineSign.js.
export function lockedMessage(me) {
    if (!me?.docs?.clickwrap) return docsPreparingMessage();
    return me.role === "owner" ? docsLockedMessage() : docsAskOwnerMessage();
}

// Mismo tope que MAX_SAMPLES_LOCKED del backend (core/engine_sample.py): con el candado puesto,
// la lista ya tiene las carteras sin archivar, y a partir de 2 de ejemplo el servidor da 409.
export const MAX_SAMPLES_LOCKED = 2;
export const sampleCapReached = (me, portfolios) =>
    isLocked(me) && Array.isArray(portfolios) && portfolios.filter((p) => p.is_sample).length >= MAX_SAMPLES_LOCKED;

// Espejo de _same_sample_data (backend/routes/engine.py): con el candado las claves de los topes
// no cambian, asi que solo se edita el valor de los ya guardados y ninguno se puede vaciar.
export const sampleCapEditable = (group, savedCaps, locked) => !locked || (savedCaps != null && group in savedCaps);
export const sampleCapIssues = (savedCaps, raw, locked) => (!locked ? [] : Object.keys(savedCaps || {})
    .filter((g) => `cap:${g}` in raw && String(raw[`cap:${g}`]).trim() === "")
    .map((g) => t("lib.sampleCaps.cantEmpty", { group: g })));

export const isDocsUnsigned = (error) =>
    error?.response?.status === 403 && error.response.data?.detail?.code === "docs_unsigned";

// loc[-1] de los errores 422 de pydantic -> etiqueta del campo en la pantalla (engine:lib.field.*).
const LABELLED_FIELDS = new Set([
    "name", "client_ref", "universe", "current_weights_pct", "k_max", "k_min", "w_min_pct", "w_max_pct", "group_caps_pct",
    "max_vol_annual_pct", "risk_aversion", "turnover_cost_bps", "max_turnover_pct", "lookback_days", "time_limit_sec",
    "symbol", "isin", "group", "weights_pct", "prep_minutes", "note",
]);
const fieldLabel = (field) => (LABELLED_FIELDS.has(field) ? t(`lib.field.${field}`) : null);

// detail = {code, message}: el backend ya manda el message en el idioma de la peticion, y el code
// es siempre el real (bad_code, nunca su variante de texto bad_code_one). Solo docs_unsigned se
// traduce aqui, por codigo. El "Value error, …" de pydantic se deja como viene.
export function apiErrorMessage(error, fallback) {
    if (isDocsUnsigned(error)) return docsLockedNeutral();
    const d = error?.response?.data?.detail;
    if (typeof d === "string") return d;
    if (d && typeof d === "object" && !Array.isArray(d) && d.message) return d.message;
    if (Array.isArray(d) && /^Value error, /.test(String(d[0]?.msg))) return String(d[0].msg).replace(/^Value error, /, "");
    const label = Array.isArray(d) && Array.isArray(d[0]?.loc) ? fieldLabel(d[0].loc[d[0].loc.length - 1]) : null;
    if (label && d[0].msg) return `${label}: ${d[0].msg}`;
    return fallback;
}

export const readOnlyText = (org, byRole = false) =>
    byRole ? t("lib.readOnly.role") : org.status === "suspended"
        ? t("lib.readOnly.suspended")
        : t("lib.readOnly.pilotEnded", { date: formatDate(org.pilot_ends_at) });

export const ENGINE_CONTACT = "support@qcap.es";

// Aviso al 80 % de cualquier cupo: al 100 % ya no se puede calcular o crear, y el
// asesor tiene que saberlo antes de quedarse a medias con un cliente delante.
export const QUOTA_WARN_PCT = 80;

export function quotaItems(org, limits, usage) {
    // Igual que el backend (org.get("seats", 1)): las plazas son las del despacho.
    const seats = org?.seats ?? 1;
    return [
        { key: "runs", label: t("lib.quota.runs"), used: usage?.runs_today, max: limits?.runs_per_day },
        { key: "portfolios", label: t("lib.quota.portfolios"), used: usage?.portfolios, max: limits?.max_portfolios },
        { key: "seats", label: t("lib.quota.seats"), used: usage?.seats_taken, max: seats },
    ]
        .filter((q) => typeof q.used === "number" && q.max > 0)
        .map((q) => {
            // Un plan de una plaza va lleno por diseño: no hay nada que corregir.
            const alone = q.key === "seats" && q.max === 1;
            return {
                ...q,
                pct: Math.min(100, Math.round((q.used / q.max) * 100)),
                near: !alone && q.used / q.max >= QUOTA_WARN_PCT / 100,
                full: !alone && q.used >= q.max,
            };
        });
}

// El contador de cálculos se reinicia a las 00:00 UTC; en Madrid es la 01:00 en
// invierno y las 02:00 en verano, así que se calcula en vez de escribirlo.
export function resetTimeMadrid(now = new Date()) {
    const next = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1));
    return new Intl.DateTimeFormat(currentLocale(), { timeZone: "Europe/Madrid", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(next);
}

export const isOwner = (me) => me?.role === "owner";

// Lo que la portada dice de cada cartera. "modified" es la señal de que toca recalcular, y manda
// sobre "failed": tras un fallo lo normal es cambiar algo y volver a calcular.
export function portfolioState(p) {
    const last = p?.last_run;
    if (!last) return "never";
    if (!isFinalStatus(last.status)) return "running";
    if (new Date(p.updated_at) > new Date(last.created_at)) return "modified";
    return last.status === "failed" ? "failed" : "done";
}

const fold = (v) => String(v ?? "").normalize("NFD").replace(/\p{M}/gu, "").toLowerCase().trim();

export function filterPortfolios(list, { query = "", state = "all", tag = "" } = {}) {
    const q = fold(query);
    return list.filter((p) => (state === "all" || portfolioState(p) === state)
        && (!tag || (p.tags || []).includes(tag))
        && (!q || fold(p.name).includes(q) || fold(p.client_ref).includes(q)));
}

export const allTags = (list) => [...new Set(list.flatMap((p) => p.tags || []))].sort((a, b) => a.localeCompare(b));

const PENDING_STATES = ["failed", "modified", "never"];

// Agrupa lo que ya trae la lista por el codigo seudonimo: no hay ficha de cliente (D-B3). Lo pendiente va primero y lo
// que no tiene codigo, al final.
export function groupByClient(list) {
    const groups = new Map();
    list.forEach((p) => {
        const ref = (p.client_ref || "").toUpperCase();
        groups.set(ref, [...(groups.get(ref) || []), p]);
    });
    return [...groups].map(([ref, portfolios]) => {
        const counts = stateCounts(portfolios);
        const reviewed = portfolios.filter((p) => p.last_run?.status === "done").map((p) => p.last_run.created_at).sort().pop() || null;
        return { ref, portfolios, counts, reviewed, pending: PENDING_STATES.reduce((n, s) => n + (counts[s] || 0), 0) };
    }).sort((a, b) => Boolean(b.ref) - Boolean(a.ref) || b.pending - a.pending || a.ref.localeCompare(b.ref));
}

// Cada fila conserva su indice en el universo: editar o quitar actua sobre ese, no sobre la posicion filtrada.
export function filterUniverse(universe, { query = "", group = null } = {}) {
    const q = fold(query);
    return universe.map((item, index) => ({ item, index })).filter(({ item }) => (!group || groupOf(item) === group)
        && (!q || fold(item.symbol).includes(q) || fold(item.name).includes(q) || fold(item.group).includes(q)));
}

export function stateCounts(list) {
    const counts = { all: list.length };
    list.forEach((p) => {
        const s = portfolioState(p);
        counts[s] = (counts[s] || 0) + 1;
    });
    return counts;
}

const lastActivity = (p) => Math.max(new Date(p.updated_at).getTime() || 0, new Date(p.last_run?.created_at).getTime() || 0);

const PORTFOLIO_SORTS = {
    recent: (a, b) => lastActivity(b) - lastActivity(a),
    name: (a, b) => String(a.name).localeCompare(String(b.name), currentLocale(), { sensitivity: "base" }),
    size: (a, b) => (b.universe?.length || 0) - (a.universe?.length || 0),
};
export const PORTFOLIO_SORT_KEYS = Object.keys(PORTFOLIO_SORTS);

export const sortPortfolios = (list, sort = "recent") => [...list].sort(PORTFOLIO_SORTS[sort]);

const NAV_BY_SEGMENT = { calculos: "runs", equipo: "team", actividad: "activity", firma: "sign", ayuda: "help" };

// Cartera y comparar cuelgan de "Carteras": es de donde se llega a ellos.
export const engineNavKey = (pathname) => NAV_BY_SEGMENT[String(pathname).split("/")[3]] || "portfolios";

// Tras el login un miembro del Engine aterriza en el Engine, salvo en la app nativa
// (el Engine no está enlazado allí) o si venía de una ruta concreta.
export const loginLanding = (target, { member, native }) =>
    target === "/app/dashboard" && member && !native ? "/app/engine" : target;

// Cartera de ejemplo para ver un cálculo completo en un minuto. Sin ISIN salvo el de
// IWDA.AS (el de la plantilla CSV): un ISIN escrito a mano es fácil que no exista. Vive
// en un JSON para que el backend compruebe en sus tests que la acepta.
export function samplePortfolio() {
    return {
        name: SAMPLE.name,
        client_ref: null,
        universe: SAMPLE.groups.flatMap(({ group, symbols }) => symbols.map((symbol) => ({
            symbol, isin: SAMPLE.isins[symbol] || null, name: null, asset_class: "etf", group,
        }))),
        current_weights_pct: {},
        constraints: {
            ...defaultConstraints(), ...SAMPLE.constraints,
            group_caps_pct: { ...SAMPLE.constraints.group_caps_pct },
        },
    };
}

export const isValidSymbol = (s) => SYMBOL_RE.test(String(s || ""));

// Con hora: dos cálculos del mismo día tienen que poder distinguirse en el historial.
export const formatWhen = (v) => (v
    ? new Date(v).toLocaleString(currentLocale(), { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "—");

// Importes como los escribe un asesor en España: "100.000", "100.000,50", "1.250.000 €".
// parseNum leería "100.000" como 100.
export function parseAmount(v, lang = currentLang()) {
    const t = String(v ?? "").replace(/[\s€]/g, "");
    if (!t) return 0;
    if (lang === "en") return parseNumStrict(t, "en");
    if (t.includes(",") && t.includes(".")) return parseNum(t.replace(/\./g, ""), "es");
    if (/^\d{1,3}(\.\d{3})+$/.test(t)) return parseNum(t.replace(/\./g, ""), "es");
    return parseNum(t, "es");
}

// Mismo tope que MAX_WEALTH_EUR en routes/engine.py: por encima, proposal.pdf da 422.
export const MAX_WEALTH_EUR = 1e10;
export const wealthTooHigh = (wealth) => wealth > MAX_WEALTH_EUR;

// El PDF sale en el idioma pedido (`lang` manda sobre el de la peticion); los nombres de fichero
// coinciden con los que pone el backend. El PDF firmado no entra: son los textos en es.
export function pdfQuery(wealth, lang = currentLang()) {
    const q = new URLSearchParams();
    if (wealth > 0) q.set("wealth", wealth);
    q.set("lang", lang);
    return `?${q}`;
}

const DOWNLOAD_NAMES = {
    runs: { es: "calculos-{id}.csv", en: "calculations-{id}.csv" },
    audit: { es: "expediente-{id}.pdf", en: "audit-file-{id}.pdf" },
    proposal: { es: "propuesta-{id}.pdf", en: "proposal-{id}.pdf" },
    comparison: { es: "comparativa-{id}.pdf", en: "comparison-{id}.pdf" },
    amounts: { es: "propuesta-importes-{id}.csv", en: "proposal-amounts-{id}.csv" },
};
export const downloadName = (kind, runId, lang = currentLang()) =>
    DOWNLOAD_NAMES[kind][lang === "en" ? "en" : "es"].replace("{id}", runId);

// Las paginas del Engine: hasta dos decimales y la agrupacion de siempre (ver engineCharts). El % lo pone el catalogo:
// «34 %» en es, «34%» en en, igual que en los graficos.
export const fmtPct = (v) => chartPct(v, currentLang(), { grouping: "locale", min: 0, max: 2 });

export const inUniverse = (universe, symbol) =>
    universe.some((u) => String(u.symbol).toUpperCase() === String(symbol || "").toUpperCase());

// Mismo reparto que backend/services/engine_personal.py para la cartera personal; los
// proveedores de la búsqueda devuelven además tipos como "Common Stock" o "EQUITY".
export function groupForType(type) {
    const t = String(type || "").toLowerCase();
    if (t.includes("etf") || t.includes("etp")) return "ETF";
    if (t.includes("pension")) return "Planes de pensiones";
    if (t.includes("fund") || t.includes("fondo")) return "Fondos";
    if (t.includes("bond") || t.includes("fixed")) return "Renta fija";
    if (t.includes("crypto")) return "Criptomonedas";
    if (t.includes("commodit")) return "Materias primas";
    if (t.includes("stock") || t.includes("equity") || t.includes("share")) return "Renta variable";
    return "Sin grupo";
}

// Resultado de /api/market/search -> fila del universo. El símbolo tiene que pasar el
// patrón de UniverseItem: si no, el backend rechazaría la cartera entera al guardar.
export function searchResultToItem(it, group) {
    const symbol = String(it?.symbol || "").trim();
    if (!isValidSymbol(symbol))
        return { item: null, error: t("lib.search.badSymbol", { symbol: symbol || "?" }) };
    const isin = String(it?.isin || "").trim().toUpperCase();
    const g = String(group || "").trim().slice(0, 40);
    return {
        isinDropped: Boolean(isin) && !isValidIsin(isin),
        item: {
            symbol,
            isin: isValidIsin(isin) ? isin : null,
            name: it?.name ? String(it.name).slice(0, 120) : null,
            asset_class: it?.type ? String(it.type).slice(0, 24) : null,
            group: g || groupForType(it?.type),
        },
        error: null,
    };
}

// --- Propuesta en euros (presentación: no entra en el expediente) -----------------

const MINOR_UNITS = { GBp: ["GBP", 100], GBX: ["GBP", 100], ZAc: ["ZAR", 100], ILA: ["ILS", 100] };

// Cotización -> {price, currency} en unidades mayores (Londres cotiza en peniques).
export function normaliseQuote(q) {
    const price = Number(q?.price);
    if (!(price > 0)) return null;
    const raw = String(q?.currency || "");
    const [currency, div] = MINOR_UNITS[raw] || [raw.toUpperCase(), 1];
    if (!/^[A-Z]{3}$/.test(currency)) return null;
    return { price: price / div, currency };
}

// Precio en euros, o null si falta la cotización o el cambio: sin precio fiable no se
// estiman participaciones (mejor un hueco que un número inventado).
export function priceInEur(quote, fxToEur) {
    const q = normaliseQuote(quote);
    if (!q) return null;
    if (q.currency === "EUR") return q.price;
    const rate = Number(fxToEur?.[q.currency]);
    return rate > 0 ? q.price * rate : null;
}

// Redondeo simétrico (-0,005 -> -0,01, no -0,00) y sin "-0".
const roundTo = (x, nd) => {
    const f = 10 ** nd;
    return (Math.sign(x) * Math.round(Math.abs(x) * f)) / f || 0;
};
export const cents = (x) => roundTo(x, 2);
export const roundUnits = (x) => roundTo(x, 2);

export function proposalAmounts(result, currentWeights, wealth, pricesEur = {}) {
    const w = Number(wealth);
    if (!(w > 0) || !result) return { rows: [], cash_eur: 0, current_cash_eur: 0 };
    const target = result.weights_pct || {};
    const current = currentWeights || {};
    const fixed = new Set((result.fixed || []).map((f) => f.symbol));
    const symbols = [...new Set([...Object.keys(target), ...Object.keys(current).filter((k) => current[k] > 0)])];
    const rows = symbols.map((symbol) => {
        const targetEur = cents((w * (target[symbol] || 0)) / 100);
        const currentEur = cents((w * (current[symbol] || 0)) / 100);
        const tradeEur = cents(targetEur - currentEur);
        const price = pricesEur[symbol] > 0 ? pricesEur[symbol] : null;
        return {
            symbol, fixed: fixed.has(symbol), target_pct: target[symbol] || 0, current_pct: current[symbol] || 0,
            current_eur: currentEur, target_eur: targetEur, trade_eur: tradeEur, price_eur: price,
            units: price ? roundUnits(targetEur / price) : null,
            trade_units: price ? roundUnits(tradeEur / price) : null,
        };
    }).sort((a, b) => b.target_eur - a.target_eur || b.current_eur - a.current_eur || a.symbol.localeCompare(b.symbol));
    const sum = (k) => rows.reduce((s, r) => s + r[k], 0);
    // Restos de redondeo por debajo de 1 € no son liquidez: se esconden.
    const leftover = (x) => (Math.abs(x) < 1 ? 0 : cents(x));
    return { rows, cash_eur: leftover(w - sum("target_eur")), current_cash_eur: leftover(w - sum("current_eur")) };
}

// es: coma decimal (con ";" de separador); en: punto decimal (con ","). Sin separador de miles.
const csvNum = (x, nd = 2) => {
    if (x == null) return "";
    const s = Number(x).toFixed(nd);
    return currentLang() === "en" ? s : s.replace(".", ",");
};
// Excel ejecuta como fórmula una celda que empieza por = + - @ (y el patrón de símbolos admite = y -).
const csvText = (s) => (/^[=+\-@\t\r]/.test(String(s)) ? `'${s}` : String(s));

const CSV_COLUMNS = ["asset", "targetPct", "currentEur", "targetEur", "tradeEur", "priceEur", "targetUnits", "tradeUnits"];

export function proposalCsv({ rows, cash_eur, current_cash_eur }) {
    const head = CSV_COLUMNS.map((k) => t(`lib.csvOut.${k}`));
    const lines = rows.map((r) => [csvText(r.symbol), csvNum(r.target_pct), csvNum(r.current_eur), csvNum(r.target_eur),
        csvNum(r.trade_eur), csvNum(r.price_eur, 4), csvNum(r.units), csvNum(r.trade_units)]);
    if (cash_eur || current_cash_eur) {
        lines.push([t("lib.csvOut.cash"), "", csvNum(current_cash_eur), csvNum(cash_eur), csvNum(cents(cash_eur - current_cash_eur)), "", "", ""]);
    }
    const sep = currentLang() === "en" ? "," : ";";
    return [head, ...lines].map((l) => l.join(sep)).join("\r\n") + "\r\n";
}

// El nombre de una cartera lo escribe el asesor: puede llevar el separador o comillas.
const csvQuoted = (s) => {
    const v = csvText(s ?? "");
    return /[";,\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
};
// Fecha y hora locales en el formato que Excel ordena bien en cualquier idioma.
const csvWhen = (v) => (v ? new Date(v).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" }) : "");

const RUNS_CSV_COLUMNS = ["when", "portfolio", "clientRef", "status", "author", "positions", "returnPct", "volatilityPct", "gapBps", "seconds", "error"];

export function runsCsv(rows) {
    const head = RUNS_CSV_COLUMNS.map((k) => t(`lib.csvRuns.${k}`));
    const lines = rows.map((r) => {
        const m = r.result?.metrics;
        return [csvWhen(r.created_at), csvQuoted(r.portfolio_name), csvQuoted(r.client_ref), csvQuoted(statusLabel(r.status)), csvQuoted(r.author),
            m?.positions ?? "", csvNum(m?.expected_return_annual_pct), csvNum(m?.volatility_annual_pct),
            csvNum(r.result?.certificate?.gap_bps_annual), csvNum(r.duration_sec, 1), r.status === "failed" ? csvQuoted(runFailureText(r)) : ""];
    });
    const sep = currentLang() === "en" ? "," : ";";
    return [head, ...lines].map((l) => l.join(sep)).join("\r\n") + "\r\n";
}

// Cada dia del filtro es el del navegador: el servidor recibe el instante exacto en que empieza.
const dayStart = (day, plusDays = 0) => {
    const d = new Date(`${day}T00:00:00`);
    if (Number.isNaN(d.getTime())) return undefined;
    d.setDate(d.getDate() + plusDays);
    return d.toISOString();
};

export function runsParams({ portfolio, status, author, from, to, batch }) {
    const params = { portfolio_id: portfolio, status, author, batch_id: batch, date_from: from && dayStart(from), date_to: to && dayStart(to, 1) };
    return Object.fromEntries(Object.entries(params).filter(([, v]) => v));
}

export function fmtDuration(seconds) {
    const total = Math.round(seconds);
    return total < 60 ? `${total} s` : `${Math.floor(total / 60)} min ${total % 60} s`;
}

// Dias UTC del cupo: formatearlos en la zona del navegador los correria uno hacia atras.
export const formatDay = (day) => new Date(`${day}T00:00:00Z`)
    .toLocaleDateString(currentLocale(), { day: "numeric", month: "short", timeZone: "UTC" });

export const activityText = (row) => t(`activity.action.${row.action}`, {
    actor: row.actor || t("activity.qcapital"), name: row.target?.name || "", defaultValue: row.action,
});

export function activityLink({ target }) {
    if (target?.type === "run") return `/app/engine/calculos/${target.id}`;
    if (target?.type === "batch") return `/app/engine/calculos?batch=${target.id}`;
    return target?.type === "portfolio" && target.open ? `/app/engine/carteras/${target.id}` : null;
}

// --- Comparar dos cálculos ---------------------------------------------------------

export function activeConstraints(run) {
    const r = run?.result;
    if (!r) return [];
    const snap = run.portfolio_snapshot?.constraints || {};
    const c = r.constraints || {};
    const out = [];
    if (snap.k_max != null && r.metrics.positions >= snap.k_max) out.push(t("lib.active.kMax", { n: snap.k_max }));
    if (c.at_w_max?.length) out.push(t("lib.active.atWMax", { count: c.at_w_max.length, list: c.at_w_max.join(", ") }));
    if (c.at_w_min?.length) out.push(t("lib.active.atWMin", { count: c.at_w_min.length, list: c.at_w_min.join(", ") }));
    if (c.var_active) out.push(t("lib.active.varActive"));
    (c.groups || []).filter((g) => g.active).forEach((g) =>
        out.push(t("lib.active.group", { group: g.label || g.index, cap: fmtPct(snap.group_caps_pct?.[g.label] ?? g.cap * 100) })));
    return out;
}

// [clave, es porcentaje]; la etiqueta es engine:lib.metric.<clave>.
const METRICS = [
    ["positions", false],
    ["expected_return_annual_pct", true],
    ["volatility_annual_pct", true],
    ["turnover_pct", true],
];
const SETTINGS = [
    "k_max", "k_min", "w_min_pct", "w_max_pct", "max_vol_annual_pct", "risk_aversion", "turnover_cost_bps", "max_turnover_pct",
    "lookback_days",
];
const same = (x, y) => JSON.stringify(x ?? null) === JSON.stringify(y ?? null);

export function diffRuns(a, b) {
    // Los pesos activo por activo no van aqui: son compareWeightRows (lib/engineCharts), que pinta el grafico.
    const ra = a.result;
    const rb = b.result;
    const metrics = METRICS.map(([key, isPct]) => {
        const x = ra.metrics?.[key] ?? null;
        const y = rb.metrics?.[key] ?? null;
        return { key, label: t(`lib.metric.${key}`), isPct, a: x, b: y, delta: x == null || y == null ? null : roundTo(y - x, 4) };
    });
    const sa = a.portfolio_snapshot?.constraints || {};
    const sb = b.portfolio_snapshot?.constraints || {};
    const settings = SETTINGS.filter((k) => !same(sa[k], sb[k]))
        .map((key) => ({ key, label: t(`lib.setting.${key}`), a: sa[key] ?? null, b: sb[key] ?? null }));
    const caps = [...new Set([...Object.keys(sa.group_caps_pct || {}), ...Object.keys(sb.group_caps_pct || {})])].sort()
        .filter((g) => !same(sa.group_caps_pct?.[g], sb.group_caps_pct?.[g]))
        .map((g) => ({ key: `cap:${g}`, label: t("lib.setting.cap", { group: g }), a: sa.group_caps_pct?.[g] ?? null, b: sb.group_caps_pct?.[g] ?? null }));
    const ua = (a.portfolio_snapshot?.universe || []).map((u) => u.symbol);
    const ub = (b.portfolio_snapshot?.universe || []).map((u) => u.symbol);
    return {
        metrics,
        gap: { a: ra.certificate?.gap_bps_annual ?? null, b: rb.certificate?.gap_bps_annual ?? null },
        trades: { a: ra.trades || [], b: rb.trades || [] },
        constraints: { a: activeConstraints(a), b: activeConstraints(b) },
        settings: [...settings, ...caps],
        universe: { added: ub.filter((s) => !ua.includes(s)), removed: ua.filter((s) => !ub.includes(s)) },
    };
}

// El ultimo calculo terminado antes de este. Uno fallido no tiene nada con lo que comparar.
export function previousDone(runs, runId) {
    const current = (runs || []).find((r) => r.id === runId);
    if (!current) return null;
    const older = runs.filter((r) => r.status === "done" && String(r.created_at) < String(current.created_at));
    return older.sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)))[0] || null;
}

// Lo que cambio respecto al anterior, sin las metricas iguales. null si falta alguna de las dos: nada que decir.
export function runDeltas(previous, current) {
    const before = previous?.result?.metrics;
    const after = current?.result?.metrics;
    if (!before || !after) return null;
    return METRICS.map(([key, isPct]) => ({
        key, isPct, label: t(`lib.metric.${key}`),
        delta: before[key] == null || after[key] == null ? 0 : roundTo(after[key] - before[key], 2),
    })).filter((d) => d.delta !== 0);
}

export const signedNumber = (v) => `${v > 0 ? "+" : ""}${Number(v).toLocaleString(currentLocale(), { maximumFractionDigits: 2 })}`;

// --- Propuesta habitual del despacho (piloto) ---------------------------------------

export const shadowHint = () => t("lib.shadow.hint");

// Mismo texto que la nota del expediente / de la plantilla del informe del piloto.
export const boundNote = () => t("lib.boundNote");

// CSV `symbol,weight_pct` o `símbolo;peso` (cabecera opcional; coma o punto y coma; coma decimal admitida).
export function parseShadowCsv(text, universeSymbols) {
    const known = new Map((universeSymbols || []).map((s) => [String(s).toUpperCase(), s]));
    const weights = {};
    const errors = [];
    const lines = String(text || "").replace(/^﻿/, "").split(/\r?\n/).filter((l) => l.trim());
    // Cabecera opcional, en inglés o en español (símbolo/simbolo; peso/peso_pct).
    if (lines.length && /^\s*(symbol|s[ií]mbolo)\s*([;,]|$)/i.test(lines[0])) lines.shift();
    lines.forEach((line) => {
        const sep = line.includes(";") ? ";" : ",";
        const cells = line.split(sep).map((x) => x.trim());
        if (cells.length > 2) { errors.push(t("lib.shadowCsv.tooManyFields", { line: line.trim() })); return; }
        const [rawSym, rawW] = cells;
        const symbol = known.get(String(rawSym || "").toUpperCase());
        const w = Number(String(rawW ?? "").replace(",", "."));
        if (!symbol) { errors.push(t("lib.shadowCsv.unknownSymbol", { symbol: rawSym || "?" })); return; }
        if (!(rawW ?? "") || !Number.isFinite(w) || w < 0 || w > 100) { errors.push(t("lib.shadowCsv.badWeight", { symbol, raw: rawW ?? "" })); return; }
        if (w === 0) return;
        weights[symbol] = (weights[symbol] || 0) + w;
    });
    const total = Object.values(weights).reduce((a, b) => a + b, 0);
    if (Object.keys(weights).length && Math.abs(total - 100) > 0.01)
        errors.push(t("lib.shadowCsv.sum", { total: fmtPct(total) }));
    return { weights, errors };
}

// Símbolos del universo cuyo peso escrito no es un número (vacío vale 0). Se avisa de ellos antes que de
// cualquier suma: un «no suma 100» no diría al asesor qué campo corregir.
export const invalidWeightSymbols = (rawWeights, universeSymbols) =>
    (universeSymbols || []).filter((s) => Number.isNaN(parseNumStrict(rawWeights?.[s])));

const notNumberIssue = (key, symbols) => t(key, { count: symbols.length, list: shortList(symbols) });

export const currentWeightsIssue = (rawWeights, universeSymbols) => {
    const bad = invalidWeightSymbols(rawWeights, universeSymbols);
    return bad.length ? notNumberIssue("lib.weights.currentNotNumber", bad) : null;
};

// Aviso de la propuesta habitual antes de calcular (null = nada que avisar). weights: {símbolo: peso > 0}.
export function shadowIssue(weights, universeSymbols, minutesText = "") {
    const keys = Object.keys(weights || {});
    if (!keys.length) return null;
    const notNumbers = keys.filter((k) => Number.isNaN(weights[k]));
    if (notNumbers.length) return notNumberIssue("lib.weights.proposalNotNumber", notNumbers);
    const known = new Set((universeSymbols || []).map((s) => String(s).toUpperCase()));
    const unknown = keys.filter((k) => !known.has(k.toUpperCase()));
    if (unknown.length) return t("lib.shadow.unknownAssets", { list: shortList(unknown) });
    const total = keys.reduce((s, k) => s + weights[k], 0);
    if (Math.abs(total - 100) > 0.01) return t("lib.shadow.sum", { total: fmtPct(total) });
    const m = String(minutesText).trim() === "" ? null : parseNumStrict(minutesText);
    if (Number.isNaN(m)) return t("lib.shadow.minutesNaN");
    if (m != null && (m < 0 || m > 1440)) return t("lib.shadow.minutesRange");
    return null;
}

// Mismo texto fijo que el informe del piloto (definición de la fila «Tiempo de preparación»).
export const prepNote = () => t("lib.prepNote");
// El detalle técnico del fallo va al log del servidor, no al asesor.
export const shadowError = () => t("lib.shadow.error");
export const seenBeforeNote = () => t("lib.shadow.seenBefore");

// Propuesta registrada que aún no se ha adjuntado a ningún cálculo (la más reciente).
export function pendingShadow(list) {
    const free = (list || []).filter((s) => s && s.run_id == null);
    if (!free.length) return null;
    return free.reduce((a, b) => (String(b.created_at || "") > String(a.created_at || "") ? b : a));
}

// Minutos entre el inicio y el fin del cálculo (R3: tiempo de preparación del Engine).
export function runMinutes(run) {
    const a = Date.parse(run?.created_at);
    const b = Date.parse(run?.finished_at);
    if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return null;
    return (b - a) / 60000;
}

const nz = (v, f) => (v == null || !Number.isFinite(Number(v)) ? "—" : f(Number(v)));
const num1 = (v) => fmtNumber(v, currentLang(), { grouping: "locale", min: 0, max: 1 });

// Coste del Engine en pb: mismo criterio que cost_bps de la propuesta del despacho
// (coste por operación x rotación de la parte optimizada, sin multiplicar por ella).
export function engineCostBps(run) {
    const c = Number(run?.portfolio_snapshot?.constraints?.turnover_cost_bps);
    const t = Number(run?.result?.constraints?.turnover);
    if (run?.portfolio_snapshot?.constraints?.turnover_cost_bps == null || run?.result?.constraints?.turnover == null) return null;
    return Number.isFinite(c) && Number.isFinite(t) ? c * t : null;
}

// Las 6 filas de §2 de la plantilla del informe. `engine` = {gap_bps_annual, prep_minutes}.
export function shadowRows(shadow, metrics, engine = {}) {
    const s = shadow || {};
    const m = metrics || {};
    const cost = (rot, bps) => (bps == null
        ? nz(rot, fmtPct)
        : t("lib.shadowRows.costWithBps", { pct: nz(rot, fmtPct), bps: num1(Number(bps)) }));
    const ok = () => {
        if (s.constraints_ok === true) return t("lib.shadowRows.yes");
        if (s.constraints_ok !== false) return "—";
        return s.violations?.length ? t("lib.shadowRows.noWith", { list: s.violations.join("; ") }) : t("lib.shadowRows.no");
    };
    return [
        { label: t("lib.shadowRows.positions"), firm: nz(s.positions, String), engine: nz(m.positions, String) },
        { label: t("lib.shadowRows.constraintsOk"), firm: ok(), engine: t("lib.shadowRows.yes") },
        { label: t("lib.shadowRows.volatility"), firm: nz(s.volatility_annual_pct, fmtPct), engine: nz(m.volatility_annual_pct, fmtPct) },
        { label: t("lib.shadowRows.turnover"), firm: cost(s.turnover_pct, s.cost_bps), engine: cost(m.turnover_pct, engine.cost_bps) },
        { label: t("lib.shadowRows.prep"), firm: nz(s.prep_minutes, num1), engine: nz(engine.prep_minutes, num1) },
        {
            label: t("lib.shadowRows.gap"),
            firm: s.constraints_ok === false || s.gap_bps_annual == null ? t("lib.shadowRows.notApplicable") : formatGap(s.gap_bps_annual),
            engine: nz(engine.gap_bps_annual, formatGap),
        },
    ];
}

export const quotaBlockMessage = () => t("lib.quotaBlock");

// El nombre del plan viaja en español desde el backend (b2b_plans); aqui se traduce por su clave.
export const planName = (plan, fallback) => (plan && i18n.exists(`engine:plan.names.${plan}`) ? t(`plan.names.${plan}`) : fallback);

// engine_runs.error se guarda en español y no se reescribe: aqui solo se traduce para mostrarlo.
// Los literales fijos son los de backend/services/engine_runs.py (el catalogo es los repite).
const FIXED_FAILURES = ["timeout", "queueTimeout", "stale", "internal"];

export function runFailureText(run) {
    const error = run?.error;
    if (!error) return t("run.failure.unknown");
    if (currentLang() === "es") return error;
    const key = FIXED_FAILURES.find((k) => ES(`run.failure.${k}`) === error)
        || (String(error).includes("interrumpio") ? "interrupted" : null);
    return key ? t(`run.failure.${key}`) : t("run.failure.other", { original: error });
}

// Si falta algún dato no se bloquea: el servidor repite la comprobación al lanzar el cálculo.
export function canRegisterShadow(me) {
    const { runs_today: today, in_flight: inFlight } = me?.usage || {};
    const { runs_per_day: perDay, max_in_flight: maxInFlight } = me?.limits || {};
    if (Number.isFinite(perDay) && Number.isFinite(today) && today >= perDay) return false;
    if (Number.isFinite(maxInFlight) && Number.isFinite(inFlight) && inFlight >= maxInFlight) return false;
    return true;
}

export const POLL_MAX_5XX = 6;
export const POLL_RETRY_MS = 5000;
const POLL_RETRY_MAX_MS = 30000;

// Solo cuentan los 5xx y los cortes de red seguidos (los 4xx se resuelven aparte y un éxito reinicia la cuenta).
export function pollBackoff(failures5xx) {
    const n = Math.max(0, failures5xx || 0);
    return { stop: n >= POLL_MAX_5XX, delayMs: Math.min(POLL_RETRY_MS * 2 ** Math.max(0, n - 1), POLL_RETRY_MAX_MS) };
}
